import { Box, HStack, VStack, Text, Button, Badge, Spinner } from '@chakra-ui/react';
import type { PendingAction } from '@/types/chat';

interface ChatActionCardProps {
  action: PendingAction;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirming: boolean;
}

function cardMeta(action: PendingAction): { header: string; confirmLabel: string } {
  switch (action.type) {
    case 'create':
      return { header: 'Save this recipe to your pantry?', confirmLabel: 'Save to pantry' };
    case 'update':
      return { header: `Update "${action.recipeTitle}"?`, confirmLabel: 'Update recipe' };
    case 'collection_create':
      return { header: 'Create collection', confirmLabel: 'Create' };
    case 'collection_add_recipes':
      return { header: `Add to collection: ${action.collectionTitle}`, confirmLabel: 'Add' };
    case 'collection_remove_recipe':
      return { header: `Remove from collection: ${action.collectionTitle}`, confirmLabel: 'Remove' };
    case 'collection_rename':
      return { header: 'Rename collection', confirmLabel: 'Rename' };
    case 'ingredient_substitution':
      return { header: 'Ask Chef Chat', confirmLabel: 'Ask' };
  }
}

function renderContent(action: PendingAction) {
  if (action.type === 'create') {
    const recipe = action.recipe;
    return (
      <VStack align="stretch" gap={2}>
        <Text fontWeight="semibold" fontSize="sm" color="text.primary">
          {recipe.title}
        </Text>
        {recipe.tags.length > 0 && (
          <HStack gap={1} flexWrap="wrap">
            {recipe.tags.map((tag) => (
              <Badge key={tag} size="sm" variant="subtle" colorPalette="cyan">
                {tag}
              </Badge>
            ))}
          </HStack>
        )}
        <VStack align="stretch" gap={1}>
          <Text fontSize="xs" fontWeight="medium" color="text.muted">
            Ingredients
          </Text>
          {recipe.ingredients.map((ing, i) => (
            <Text key={i} fontSize="xs" color="text.primary">
              • {[ing.quantity, ing.unit, ing.name].filter(Boolean).join(' ')}
            </Text>
          ))}
        </VStack>
        <VStack align="stretch" gap={1}>
          <Text fontSize="xs" fontWeight="medium" color="text.muted">
            Instructions
          </Text>
          {recipe.instructions.map((step, i) => (
            <Text key={i} fontSize="xs" color="text.primary">
              {i + 1}. {step}
            </Text>
          ))}
        </VStack>
      </VStack>
    );
  }

  if (action.type === 'update') {
    return (
      <VStack align="stretch" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.muted">
          Changes
        </Text>
        {action.changes.map((change, i) => (
          <Text key={i} fontSize="xs" color="text.primary">
            • {change.description}
          </Text>
        ))}
      </VStack>
    );
  }

  if (action.type === 'collection_create') {
    return (
      <VStack align="stretch" gap={2}>
        <Text fontWeight="semibold" fontSize="sm" color="text.primary">
          {action.title}
        </Text>
        {action.initialRecipeNames.length > 0 && (
          <VStack align="stretch" gap={1}>
            <Text fontSize="xs" fontWeight="medium" color="text.muted">
              Recipes to add
            </Text>
            {action.initialRecipeNames.map((name, i) => (
              <Text key={i} fontSize="xs" color="text.primary">
                • {name}
              </Text>
            ))}
          </VStack>
        )}
      </VStack>
    );
  }

  if (action.type === 'collection_add_recipes') {
    return (
      <VStack align="stretch" gap={1}>
        {action.recipeNames.map((name, i) => (
          <Text key={i} fontSize="xs" color="text.primary">
            • {name}
          </Text>
        ))}
      </VStack>
    );
  }

  if (action.type === 'collection_remove_recipe') {
    return (
      <Text fontSize="xs" color="text.primary">
        {action.recipeName}
      </Text>
    );
  }

  // collection_rename
  if (action.type === 'collection_rename') {
    return (
      <Text fontSize="xs" color="text.primary">
        Rename &ldquo;{action.oldTitle}&rdquo; to &ldquo;{action.newTitle}&rdquo;
      </Text>
    );
  }

  return null;
}

export default function ChatActionCard({
  action,
  onConfirm,
  onCancel,
  isConfirming,
}: ChatActionCardProps) {
  const { header, confirmLabel } = cardMeta(action);

  return (
    <Box
      data-testid="chat-action-card"
      mx={4}
      mb={3}
      borderWidth="1.5px"
      borderColor="accent.secondaryBorder"
      borderRadius="lg"
      bg="accent.secondarySubtle"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Fixed header */}
      <Box px={4} pt={4} pb={2} flexShrink={0}>
        <Text fontWeight="semibold" fontSize="sm" color="text.primary">
          {header}
        </Text>
      </Box>

      {/* Scrollable content — grows with content, capped at 50vh */}
      <Box overflowY="auto" maxH="50vh" px={4}>
        {renderContent(action)}
      </Box>

      {/* Fixed footer buttons */}
      <HStack gap={2} justify="flex-end" px={4} py={3} flexShrink={0}>
        <Button
          size="sm"
          variant="ghost"
          color="text.muted"
          onClick={onCancel}
          disabled={isConfirming}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          bg="accent.secondary"
          color="white"
          _hover={{ bg: 'accent.secondaryHover' }}
          onClick={onConfirm}
          disabled={isConfirming}
        >
          {isConfirming ? <Spinner size="xs" /> : confirmLabel}
        </Button>
      </HStack>
    </Box>
  );
}
